import React, { useState } from 'react';
import {
  ErrorDiagnosticData,
  formatDiagnosticAsText,
  copyToClipboardFallback
} from './errorDiagnosticService';

interface ErrorDiagnosticViewProps {
  data: ErrorDiagnosticData;
  onReload?: () => void;
}

export const ErrorDiagnosticView: React.FC<ErrorDiagnosticViewProps> = ({
  data,
  onReload
}) => {
  const [copied, setCopied] = useState(false);
  const [showFullStack, setShowFullStack] = useState(false);

  const handleCopy = async () => {
    const textToCopy = formatDiagnosticAsText(data);
    const success = await copyToClipboardFallback(textToCopy);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } else {
      alert('Impossible de copier automatiquement. Veuillez sélectionner le texte manuellement.');
    }
  };

  const handleReload = () => {
    if (onReload) {
      onReload();
    } else {
      window.location.reload();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#0a0f1d',
        color: '#f8fafc',
        zIndex: 999999,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '16px',
        paddingTop: 'max(16px, env(safe-area-inset-top, 16px))',
        paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        fontSize: '15px',
        lineHeight: 1.5,
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          maxWidth: '680px',
          margin: '0 auto',
          backgroundColor: '#131c31',
          border: '2px solid #ef4444',
          borderRadius: '12px',
          padding: '18px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.8)'
        }}
      >
        {/* En-tête */}
        <div style={{ borderBottom: '1px solid #334155', paddingBottom: '12px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#f87171', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>⚠️</span>
              <span>HockeyDynasty - Erreur</span>
            </h1>
            <span
              style={{
                backgroundColor: data.source === 'React Error Boundary' ? '#7c2d12' : '#831843',
                color: '#fecdd3',
                fontSize: '12px',
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              {data.source}
            </span>
          </div>
          <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#94a3b8' }}>
            Un problème est survenu lors de l'exécution ou de l'affichage de la page.
          </p>
        </div>

        {/* Boutons d'action rapides */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleCopy}
            style={{
              flex: '1 1 140px',
              backgroundColor: copied ? '#16a34a' : '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '15px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            <span>{copied ? '✅' : '📋'}</span>
            <span>{copied ? 'Copié dans le presse-papiers !' : 'Copier les détails'}</span>
          </button>

          <button
            type="button"
            onClick={handleReload}
            style={{
              flex: '1 1 120px',
              backgroundColor: '#475569',
              color: '#ffffff',
              border: '1px solid #64748b',
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '15px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span>🔄</span>
            <span>Recharger</span>
          </button>
        </div>

        {/* Détails principaux */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Page */}
          <div style={{ backgroundColor: '#1e293b', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Page</div>
            <div style={{ fontSize: '15px', color: '#38bdf8', fontWeight: 700, wordBreak: 'break-all' }}>
              {data.pathname || '/'}
            </div>
          </div>

          {/* Erreur */}
          <div style={{ backgroundColor: '#2a1215', padding: '12px', borderRadius: '8px', border: '1px solid #7f1d1d' }}>
            <div style={{ fontSize: '12px', color: '#fca5a5', fontWeight: 700, textTransform: 'uppercase' }}>Message d'erreur</div>
            <div style={{ fontSize: '15px', color: '#fef2f2', fontWeight: 600, wordBreak: 'break-word', marginTop: '4px' }}>
              {data.message}
            </div>
          </div>

          {/* Mode & Contexte Ligue */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div style={{ backgroundColor: '#1e293b', padding: '8px 12px', borderRadius: '8px', border: '1px solid #334155' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Mode</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: data.isEmbed ? '#fbbf24' : '#4ade80' }}>
                {data.modeText}
              </div>
            </div>

            <div style={{ backgroundColor: '#1e293b', padding: '8px 12px', borderRadius: '8px', border: '1px solid #334155' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Current League</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#f1f5f9' }}>
                {data.currentLeagueId !== null ? `#${data.currentLeagueId}` : 'Aucune'}
              </div>
            </div>
          </div>

          {/* Ligue sélectionnée détaillée */}
          <div style={{ backgroundColor: '#1e293b', padding: '8px 12px', borderRadius: '8px', border: '1px solid #334155' }}>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Ligue sélectionnée</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#f1f5f9' }}>
              {data.selectedLeague ? `${data.selectedLeague.name ?? 'Sans nom'} (ID: ${data.selectedLeague.id ?? 'N/A'})` : 'Aucune ligue sélectionnée'}
            </div>
          </div>

          {/* Viewport & Appareil */}
          <div style={{ backgroundColor: '#1e293b', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155' }}>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Viewport & Écran</div>
            <div style={{ fontSize: '13px', color: '#f1f5f9', marginTop: '2px' }}>
              <strong>Viewport :</strong> {data.viewportWidth} × {data.viewportHeight} (DPR: {data.devicePixelRatio})
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8' }}>
              <strong>Écran :</strong> {data.screenWidth} × {data.screenHeight}
            </div>
          </div>

          {/* Navigateur */}
          <div style={{ backgroundColor: '#1e293b', padding: '10px 12px', borderRadius: '8px', border: '1px solid #334155' }}>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Navigateur / User Agent</div>
            <div style={{ fontSize: '12px', color: '#cbd5e1', wordBreak: 'break-word', marginTop: '2px', fontFamily: 'monospace' }}>
              {data.userAgent}
            </div>
          </div>

          {/* Date & Heure */}
          <div style={{ backgroundColor: '#1e293b', padding: '8px 12px', borderRadius: '8px', border: '1px solid #334155' }}>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Date & Heure</div>
            <div style={{ fontSize: '13px', color: '#cbd5e1' }}>
              {data.timestamp}
            </div>
          </div>

          {/* Stack trace accordéon */}
          {(data.stack || data.componentStack) && (
            <div style={{ backgroundColor: '#0f172a', borderRadius: '8px', border: '1px solid #334155', overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setShowFullStack(!showFullStack)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  backgroundColor: '#1e293b',
                  color: '#93c5fd',
                  border: 'none',
                  padding: '10px 12px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{showFullStack ? '▼ Masquer la trace technique (Stack trace)' : '▶ Afficher la trace technique (Stack trace)'}</span>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>{showFullStack ? 'Replier' : 'Déplier'}</span>
              </button>

              {showFullStack && (
                <div style={{ padding: '12px', overflowX: 'auto' }}>
                  {data.stack && (
                    <div style={{ marginBottom: data.componentStack ? '12px' : '0' }}>
                      <div style={{ fontSize: '11px', color: '#f87171', fontWeight: 700, marginBottom: '4px' }}>STACK TRACE JS :</div>
                      <pre
                        style={{
                          margin: 0,
                          fontSize: '11px',
                          color: '#fca5a5',
                          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {data.stack}
                      </pre>
                    </div>
                  )}

                  {data.componentStack && (
                    <div>
                      <div style={{ fontSize: '11px', color: '#60a5fa', fontWeight: 700, marginBottom: '4px' }}>REACT COMPONENT STACK :</div>
                      <pre
                        style={{
                          margin: 0,
                          fontSize: '11px',
                          color: '#93c5fd',
                          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {data.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
